import React from 'react';
import { Button, Modal } from 'antd';
import { BankMark, linkedAccountTypeLabel } from '@wavvest/wealth-ui';
import { formatCurrency } from '@wavvest/shared-utils';
import { ASSET_LIABILITY } from '@/util/planning-util';
import { formatAccountType } from '@/util/account-type-format';
import { selectPlaidDisplayBalance } from '@/util/plaid-util';
import type { BankAccountData } from '@/util/plaid-util';
import { apexRemovalNotice, unlinkBlockReason } from './bank-connections';
import type { ApexAccountName, BankConnection, BankUnlinkCheckState } from './bank-connections';

type UnlinkBankModalProps = {
  open: boolean;
  banks: readonly BankConnection[];
  /** The pre-unlink check for each bank, by bank key. Absent when none is needed. */
  checks: Readonly<Record<string, BankUnlinkCheckState>>;
  /** Names the Apex accounts a bank funds; the check only has their Apex ids. */
  apexAccountName?: ApexAccountName;
  /** Key of the bank being unlinked right now, if any. */
  pendingKey: string | null;
  onClose: () => void;
  onUnlink: (bank: BankConnection) => void;
};

const accountLabel = (account: BankAccountData) =>
  linkedAccountTypeLabel(formatAccountType(account.account_type), account.mask);

const accountAmount = (account: BankAccountData) => {
  const amount = formatCurrency(selectPlaidDisplayBalance(account));
  return account.asset_or_liability === ASSET_LIABILITY.LIABILITY ? `${amount} owed` : amount;
};

const UnlinkBankModal: React.FC<UnlinkBankModalProps> = ({
  open,
  banks,
  checks,
  apexAccountName,
  pendingKey,
  onClose,
  onUnlink,
}) => (
  <Modal open={open} title="Unlink external bank" footer={null} onCancel={onClose} width={520}>
    <p className="unlink-bank-intro">Unlinking a bank removes all of its accounts together.</p>
    {banks.map((bank) => {
      const reason = unlinkBlockReason(bank, checks[bank.key]);
      const notice = reason
        ? undefined
        : apexRemovalNotice(bank, checks[bank.key], apexAccountName);
      return (
        <section key={bank.key} className="unlink-bank-card" aria-label={bank.institutionName}>
          <header className="unlink-bank-header">
            <span className="unlink-bank-name">
              <span className="unlink-bank-logo">
                <BankMark logo={bank.institutionLogo} name={bank.institutionName} />
              </span>
              {bank.institutionName}
            </span>
            <Button
              danger
              disabled={Boolean(reason) || (pendingKey !== null && pendingKey !== bank.key)}
              loading={pendingKey === bank.key}
              aria-label={`Unlink ${bank.institutionName}`}
              onClick={() => onUnlink(bank)}
            >
              Unlink
            </Button>
          </header>
          <ul className="unlink-bank-accounts">
            {bank.accounts.map((account) => (
              <li key={account.account_id}>
                <span>{accountLabel(account)}</span>
                <span className="unlink-bank-amount">{accountAmount(account)}</span>
              </li>
            ))}
          </ul>
          {reason ? <p className="unlink-bank-note">{reason}</p> : null}
          {notice ? <p className="unlink-bank-note is-info">{notice}</p> : null}
        </section>
      );
    })}
  </Modal>
);

export default UnlinkBankModal;
